import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
} from 'amazon-cognito-identity-js';
import config from '../lib/config';

const pool = new CognitoUserPool({
  UserPoolId: config.cognito.userPoolId,
  ClientId: config.cognito.clientId,
  // localStorage (not sessionStorage) so the session survives closing the tab/browser —
  // the SDK silently refreshes tokens until the refresh token expires, so users stay logged
  // in for up to the refresh-token validity (7 days) instead of having to sign in every visit.
  Storage: window.localStorage,
});

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const cognitoUserRef = useRef(null);

  useEffect(() => {
    const cognitoUser = pool.getCurrentUser();
    if (!cognitoUser) { setLoading(false); return; }

    cognitoUser.getSession((err, session) => {
      if (err || !session.isValid()) { setLoading(false); return; }
      cognitoUserRef.current = cognitoUser;
      cognitoUser.getUserAttributes((err, attrs) => {
        if (!err) {
          const attrMap = Object.fromEntries(attrs.map(a => [a.Name, a.Value]));
          setUser({
            sub: attrMap.sub,
            email: attrMap.email,
            name: attrMap.name,
            groups: session.getIdToken().payload['cognito:groups'] || [],
            token: session.getIdToken().getJwtToken(),
          });
        }
        setLoading(false);
      });
    });
  }, []);

  const login = (email, password) => new Promise((resolve, reject) => {
    const lc = email.toLowerCase();
    const cognitoUser = new CognitoUser({ Username: lc, Pool: pool, Storage: window.localStorage });
    const authDetails = new AuthenticationDetails({ Username: lc, Password: password });

    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (session) => {
        cognitoUserRef.current = cognitoUser;
        cognitoUser.getUserAttributes((err, attrs) => {
          const attrMap = Object.fromEntries((attrs || []).map(a => [a.Name, a.Value]));
          const u = {
            sub: attrMap.sub,
            email: attrMap.email,
            name: attrMap.name,
            groups: session.getIdToken().payload['cognito:groups'] || [],
            token: session.getIdToken().getJwtToken(),
          };
          setUser(u);
          resolve(u);
        });
      },
      onFailure: (err) => {
        fetch(`${config.apiUrl}/auth/event`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, reason: err.code || err.name || 'unknown' }),
        }).catch(() => {});
        reject(err);
      },
      newPasswordRequired: (userAttributes) => {
        resolve({ newPasswordRequired: true, cognitoUser, userAttributes });
      },
    });
  });

  const completeNewPassword = (cognitoUser, newPassword, displayName) => new Promise((resolve, reject) => {
    const attrs = displayName ? { name: displayName } : {};
    cognitoUser.completeNewPasswordChallenge(newPassword, attrs, {
      onSuccess: (session) => {
        cognitoUserRef.current = cognitoUser;
        cognitoUser.getUserAttributes((err, attrList) => {
          const attrMap = Object.fromEntries((attrList || []).map(a => [a.Name, a.Value]));
          const u = {
            sub: attrMap.sub,
            email: attrMap.email,
            name: displayName || attrMap.name,
            groups: session.getIdToken().payload['cognito:groups'] || [],
            token: session.getIdToken().getJwtToken(),
          };
          setUser(u);
          resolve(u);
        });
      },
      onFailure: reject,
    });
  });

  const register = (email, password, name) => new Promise((resolve, reject) => {
    const attrs = [new CognitoUserAttribute({ Name: 'name', Value: name })];
    pool.signUp(email.toLowerCase(), password, attrs, null, (err, result) => {
      if (err) reject(err);
      else resolve(result);
    });
  });

  const confirmRegistration = (email, code) => new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({ Username: email.toLowerCase(), Pool: pool });
    cognitoUser.confirmRegistration(code, true, (err, result) => {
      if (err) reject(err);
      else resolve(result);
    });
  });

  const changePassword = (oldPassword, newPassword) => new Promise((resolve, reject) => {
    const cu = cognitoUserRef.current || pool.getCurrentUser();
    if (!cu) return reject(new Error('Not logged in'));
    cu.getSession((err, session) => {
      if (err) return reject(err);
      if (!session.isValid()) return reject(new Error('Session expired'));
      cu.changePassword(oldPassword, newPassword, (err2, result) => {
        if (err2) reject(err2);
        else resolve(result);
      });
    });
  });

  const forgotPassword = (email) => new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({ Username: email.toLowerCase(), Pool: pool });
    cognitoUser.forgotPassword({
      onSuccess: resolve,
      onFailure: reject,
      inputVerificationCode: resolve,
    });
  });

  const confirmForgotPassword = (email, code, newPassword) => new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({ Username: email.toLowerCase(), Pool: pool });
    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess: resolve,
      onFailure: reject,
    });
  });

  const logout = () => {
    pool.getCurrentUser()?.signOut();
    cognitoUserRef.current = null;
    setUser(null);
  };

  const getToken = () => new Promise((resolve, reject) => {
    const cognitoUser = cognitoUserRef.current || pool.getCurrentUser();
    if (!cognitoUser) return reject(new Error('Not logged in'));
    cognitoUser.getSession((err, session) => {
      if (err) return reject(err);
      resolve(session.getIdToken().getJwtToken());
    });
  });

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, register, confirmRegistration, completeNewPassword, changePassword, forgotPassword, confirmForgotPassword, getToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
